export type PayoutMode = "CASH" | "BANK_TRANSFER" | "CHEQUE" | "UPI";

export const PAYOUT_MODE_OPTIONS: ReadonlyArray<{ id: PayoutMode; name: string }> = [
  { id: "CASH", name: "Cash" },
  { id: "BANK_TRANSFER", name: "Bank transfer" },
  { id: "UPI", name: "UPI" },
  { id: "CHEQUE", name: "Cheque" },
];

export function payoutModeLabel(mode: string | null | undefined): string {
  return PAYOUT_MODE_OPTIONS.find((o) => o.id === mode)?.name ?? mode ?? "—";
}

export type PayoutSummary = {
  paidCount: number;
  pendingCount: number;
  nothingToPayCount: number;
  paidAmount: number;
  pendingAmount: number;
};

/** Last calendar day of a `YYYY-MM` wage month, as `YYYY-MM-DD`. */
export function lastDayOfMonth(yearMonth: string): string {
  const [y, m] = yearMonth.split("-").map(Number);
  const day = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return `${yearMonth}-${String(day).padStart(2, "0")}`;
}

/** Suggested payment date: today, but never before the wage month has ended. */
export function defaultPaidOn(yearMonth: string, todayYmd: string): string {
  const end = lastDayOfMonth(yearMonth);
  return todayYmd >= end ? todayYmd : end;
}

/** Same rules as the API; returns the first problem or null. The API stays authoritative. */
export function payoutFormError(input: {
  yearMonth: string;
  paidOn: string;
  todayYmd: string;
  mode: PayoutMode;
  reference: string;
}): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(input.paidOn)) return "Enter the payment date";
  if (input.paidOn > input.todayYmd) return "The payment date cannot be in the future";
  if (input.paidOn < lastDayOfMonth(input.yearMonth)) {
    return "The payment date cannot be before the end of the wage month";
  }
  if (input.mode !== "CASH" && input.reference.trim() === "") {
    return "Enter the reference (UTR / cheque no. / UPI ref)";
  }
  return null;
}

/** Local calendar date as YYYY-MM-DD (not UTC, which is still "yesterday" early morning in IST). */
export function localTodayYmd(now: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

export function payoutSummaryText(s: PayoutSummary, fmt: (n: number) => string): string {
  const parts = [
    `${fmt(s.paidAmount)} paid (${s.paidCount})`,
    `${fmt(s.pendingAmount)} pending (${s.pendingCount})`,
  ];
  if (s.nothingToPayCount > 0) parts.push(`${s.nothingToPayCount} with nothing to pay`);
  return parts.join(" · ");
}
